import React, { useCallback } from "react";

import UserItemView from "./UserItemView";

import { useCurrentUser } from "@/features/auth";
import {
  InvitationsOrganizationUserFragment,
  useInvitationActivateUserMutation,
  useInvitationDeactivateUserMutation,
} from "@/generated/graphql";
import { getCognitoDisplayName } from "@/utils/getCognitoDisplayName";
import useRecordUserGoal, { GoalType } from "@/hooks/useRecordUserGoal";

/**
 * Props for the UserItemContainer component.
 */
interface Props {
  item: InvitationsOrganizationUserFragment;
}

/**
 * Container component for managing a single organization user with activate/deactivate functionality.
 * @param root0 - The component props
 * @param root0.item - The organization user data fragment containing user details, role, and status information
 */
const UserItemContainer = ({ item }: Props) => {
  // SE-6562: `given_name` / `family_name` are optional Cognito attributes and
  // the `user` table has no name columns, so a member legitimately has neither.
  // Resolve through the shared chain (full name → email → placeholder) instead
  // of interpolating raw attribute lookups, which rendered "undefined
  // undefined". The fragment selects both attribute shapes, so prefer
  // `Attributes` and fall back to `UserAttributes`.
  const nameDisplay = getCognitoDisplayName(
    item.user?.cognitoUser?.Attributes ??
      item.user?.cognitoUser?.UserAttributes ??
      null,
    "Unknown User"
  );
  const currentUser = useCurrentUser();
  const isCurrentUser = currentUser?.id === item.user?.id;
  const recordActivateUserGoal = useRecordUserGoal({
    goalType: GoalType.ActivateUser,
    value: item.user?.id ?? "",
  });
  const recordDeactivateUserGoal = useRecordUserGoal({
    goalType: GoalType.DeactivateUser,
    value: item.user?.id ?? "",
  });

  const [deactivate, { loading: deactivateLoading }] =
    useInvitationDeactivateUserMutation({
      onCompleted: () => {
        recordDeactivateUserGoal();
      },
      variables: { input: { userId: item.user?.id ?? "" } },
    });

  const [activate, { loading: activateLoading }] =
    useInvitationActivateUserMutation({
      onCompleted: () => {
        recordActivateUserGoal();
      },
      variables: { input: { userId: item.user?.id ?? "" } },
    });

  const loading = deactivateLoading || activateLoading;

  const handleToggle = useCallback(() => {
    if (item.status === "ACTIVE") {
      deactivate();
    } else {
      activate();
    }
  }, [item.status, deactivate, activate]);

  return (
    <UserItemView
      item={item}
      nameDisplay={nameDisplay}
      isCurrentUser={isCurrentUser}
      loading={loading}
      onToggle={handleToggle}
    />
  );
};

export default UserItemContainer;
